  import React, { useState, useRef, useEffect } from "react";
  import { db } from "./firebase";
  import { collection, addDoc } from "firebase/firestore";
  import Header from "./components/Header";
  import TimerDisplay from "./components/TimerDisplay";
  import TimerControls from "./components/TimerControls";
  import Footer from "./components/Footer";
  import { parseTime, formatTime } from "./components/timeHelpers";
  import ThemeModal from "./components/ThemeModal";
  import { useAccurateTimer } from "./components/TimerLogic";
  import CustomTimerModal from "./components/CustomTimerModal.js";
  import { useNavigate } from "react-router-dom";
  import { auth } from "./firebase.js";
  import { createTimerRoom } from "./components/CreateRoom.js";
  import useWakeLock from "./components/useWakeLock";
  import NotepadModal from "./components/NotepadModal";
  import useTimerPictureInPicture from "./components/useTimerPictureInPicture";
  import { getTimerDisplayLabel } from "./components/timeHelpers";
  
  // Time on browser tab
  function formatTabTime(totalSeconds) {
    const s = Math.max(0, Math.floor(totalSeconds));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return h > 0
      ? `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
      : `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
    }
    
  function App({ themeKey, setThemeKey, themes }) {
    // State and hooks
    const [displayMode, setDisplayMode] = useState("text");
    const [mode, setMode] = useState("settime");
    const [editing, setEditing] = useState(false);
    const [, setNotif] = useState("");
    const [inputValue, setInputValue] = useState(formatTime(25 * 60));
    const [baseSeconds, setBaseSeconds] = useState(25 * 60);
    const [running, setRunning] = useState(false);
    const inputRef = useRef();
    const alarmAudioRef = useRef();
    const [previewThemeKey, setPreviewThemeKey] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const activeThemeKey = previewThemeKey || themeKey;
    const theme = themes[activeThemeKey] || themes["coffee"];
    const [customModalOpen, setCustomModalOpen] = useState(false);  
    const [sequenceActive, setSequenceActive] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [currentPhase, setCurrentPhase] = useState("timer");
    const navigate = useNavigate();
    const [timerPairs, setTimerPairs] = useState([
      { timer: "00:00:00", break: "00:00:00", timerLabel: "TIMER", breakLabel: "BREAK" }
    ]);
    const [loopTimers, setLoopTimers] = useState(false);
    const [selectedPreset, setSelectedPreset] = useState(null);
    const sessionStartRef = useRef(null);
    const sessionTypeRef = useRef("focus"); // or "custom", "break", etc, depending on your timer
    const {
      timerSeconds,
      setTimerSeconds,
      reset,
    } = useAccurateTimer({
      running,
      mode: mode === "stopwatch" ? "stopwatch" : "timer",
      baseSeconds,
      onExpire: handleExpire,
    });
    // Keep the original tab title so we can restore it
    const originalTitleRef = useRef(document.title);

    // Restore the original title on unmount
    useEffect(() => {
      const originalTitle = originalTitleRef.current;

      return () => {
        document.title = originalTitle;
      };
    }, []);

    // Update tab title while timer is running (timer mode)
    useEffect(() => {
      // Show time on the tab for timer and stopwatch. 
      if (running || mode === "timer" || mode === "stopwatch") {
        document.title = formatTabTime(timerSeconds);
      } else {
        document.title = originalTitleRef.current;
      }
    }, [mode, running, timerSeconds]);
    
    
    
    useWakeLock(running); // Keeps screen on when timer is running

    // Notepad modal state
    const [notepadModalOpen, setNotepadModalOpen] = useState(false);
    const timerLabel = getTimerDisplayLabel({
      mode,
      running,
      currentPhase,
      selectedPreset,
      timerPairs,
      currentStep,
    });

    useTimerPictureInPicture({
      running,
      timerSeconds,
      label: timerLabel,
      theme,
    });

    function handleTimeChange(newSeconds) {
      setBaseSeconds(newSeconds);
      setTimerSeconds(newSeconds);
      setInputValue(formatTime(newSeconds));
    }
    function handleExpire() {
      setRunning(false);
      if (alarmAudioRef.current) {
        alarmAudioRef.current.currentTime = 0;
        alarmAudioRef.current.play();
      }
      
      // ADD: log session if user is logged in
      const user = auth.currentUser;
      if (
        sessionStartRef.current &&
        user &&
        sessionStartRef.current < Date.now()
      ) {
        const end = Date.now();
        const duration = Math.round((end - sessionStartRef.current) / 1000); // in seconds
        addDoc(
          collection(db, "users", user.uid, "sessions"),
          {
            startTime: new Date(sessionStartRef.current),
            endTime: new Date(end),
            duration: duration,
            date: new Date().toISOString().split("T")[0], // YYYY-MM-DD
            timerType: sessionTypeRef.current,
          }
        ).catch(err => {
          console.error("Failed to save timer session (expire):", err);
        });
      }
    
      sessionStartRef.current = null;
    }    
    

    function handleTimerClick() {
      if (running) return; // Prevent editing if running
      setEditing(true);
      setRunning(false);
      setInputValue(formatTime(timerSeconds));
      setTimeout(() => inputRef.current?.focus(), 50);
    }
    

    function handleInputBlurOrEnter() {
      const newSeconds = parseTime(inputValue);
      setTimerSeconds(newSeconds);
      setBaseSeconds(newSeconds);
      setEditing(false);
    }

    function handleInputChange(e) {
      setInputValue(e.target.value.replace(/[^\d:]/g, ""));
    }

    function handleInputKeyDown(e) {
      if (e.key === "Enter") handleInputBlurOrEnter();
      else if (e.key === "Escape") setEditing(false);
    }

    function handleStart() {
      if (!running && (mode === "stopwatch" || timerSeconds > 0)) {
        setRunning(true);
        sessionStartRef.current = Date.now();
        sessionTypeRef.current = mode === "stopwatch" ? "stopwatch" : "focus";
      }
    }
    

    async function handleShare() {
      const user = auth.currentUser;
      if (!user) {
        setNotif("You must be logged in to share a timer.");
        setTimeout(() => setNotif(""), 2500); //
        return;
      }
      try {
        const timerState = {
          running,
          baseSeconds,
          timerSeconds,
          mode,
          timerPairs,
          loopTimers,
          selectedPreset,
        };
        localStorage.setItem("tufftimer_uid", user.uid);
    
        // 1. Create the room
        const roomId = await createTimerRoom(user.uid, timerState);
    
        // 2. Copy link to clipboard
        const url = `${window.location.origin}/room/${roomId}`;
        await navigator.clipboard.writeText(url);
    
        // 3. Navigate to the room and show notification (handled in TimerRoomPage)
        navigate(`/room/${roomId}`, {
          state: { notification: "New room created and link copied to clipboard!" },
        });
      } catch (err) {
        setNotif("Share failed: " + (err?.message || err));
        setTimeout(() => setNotif(""), 2500); // 4 seconds
      }
    }
    


    function handleReset() {
      setRunning(false);
      reset(baseSeconds);
      if (alarmAudioRef.current) {
        alarmAudioRef.current.pause();
        alarmAudioRef.current.currentTime = 0;
      }
    }

    async function handleStop() {
      setRunning(false);
    
      // Only log session if timer was running, and user is logged in
      const user = auth.currentUser;
      if (
        sessionStartRef.current &&
        user &&
        sessionStartRef.current < Date.now()
      ) {
        const end = Date.now();
        const duration = Math.round((end - sessionStartRef.current) / 1000); // in seconds
    
        try {
          await addDoc(
            collection(db, "users", user.uid, "sessions"), 
            {
              startTime: new Date(sessionStartRef.current),
              endTime: new Date(end),
              duration: duration,
              date: new Date().toISOString().split("T")[0], // YYYY-MM-DD
              timerType: sessionTypeRef.current,
            }
          );
                } catch (err) {
          console.error("Failed to save timer session:", err);
        }
      }
    
      // Clear session start so it doesn't double-record
      sessionStartRef.current = null;
    }  

    function startCustomSequence() {
      setSequenceActive(true);
      setCurrentStep(0);
      setCurrentPhase("timer");
      const sec = parseTime(timerPairs[0]?.timer);
      setBaseSeconds(sec);
      setTimerSeconds(sec);
      setMode("custom");
      setRunning(true);
    }


    // When user picks a preset:
    function setPresetPair(workMinutes, breakMinutes) {
      let presetName = null;
      if (workMinutes === 25 && breakMinutes === 5) presetName = "pomodoro";
      if (workMinutes === 90 && breakMinutes === 30) presetName = "deepwork";
      setSelectedPreset(presetName);
      setTimerPairs([
        {
          timer: `${String(workMinutes).padStart(2, "0")}:00`,
          break: `${String(breakMinutes).padStart(2, "0")}:00`,
          timerLabel: "TIMER",
          breakLabel: "BREAK"
        }
      ]);
      setMode("preset");
      setSequenceActive(false);
      setCurrentStep(0);
      setCurrentPhase("timer");
      setBaseSeconds(workMinutes * 60);
      setTimerSeconds(workMinutes * 60);
      setInputValue(formatTime(workMinutes * 60));
      setRunning(false); // don't start immediately!
    }



    function handleNextTimer() {
      if (mode === "preset" && timerPairs.length === 1) {
        const single = timerPairs[0];
        const nextPhase = currentPhase === "timer" ? "break" : "timer";
        const timeStr = nextPhase === "timer" ? single.timer : single.break;
        const nextSeconds = parseTime(timeStr || "00:00");
        setCurrentPhase(nextPhase);
        setBaseSeconds(nextSeconds);
        setTimerSeconds(nextSeconds);
        setInputValue(formatTime(nextSeconds));
        setRunning(false); // Let the user start manually if needed
        return;
      }
      if (sequenceActive && timerPairs.length > 0) {
        let nextStep = currentStep;
    
        if (currentPhase === "timer" && parseTime(timerPairs[currentStep]?.break) > 0) {
          setCurrentPhase("break");
          const breakSeconds = parseTime(timerPairs[currentStep].break);
          setBaseSeconds(breakSeconds);
          setTimerSeconds(breakSeconds);
          setInputValue(formatTime(breakSeconds));
        } else {
          nextStep = currentStep + 1;
          if (nextStep < timerPairs.length) {
            setCurrentStep(nextStep);
            setCurrentPhase("timer");
            const timerSeconds = parseTime(timerPairs[nextStep].timer);
            setBaseSeconds(timerSeconds);
            setTimerSeconds(timerSeconds);
            setInputValue(formatTime(timerSeconds));
          } else if (loopTimers && timerPairs.length > 0) {
            setCurrentStep(0);
            setCurrentPhase("timer");
            const timerSeconds = parseTime(timerPairs[0].timer);
            setBaseSeconds(timerSeconds);
            setTimerSeconds(timerSeconds);
            setInputValue(formatTime(timerSeconds));
          } else {
            setSequenceActive(false);
            setRunning(false);
            return;
          }
        }
        setRunning(false); // Or set to true if you want to auto-start
      }
    }
    

    return (
      <div
        style={{
          background: theme.colors.background,
          color: theme.colors.text,
          minHeight: "100vh",
        }}
        className="flex flex-col"
      >
        <audio id="alarm-audio" src="/alarm.wav" preload="auto" loop ref={alarmAudioRef} />
        <CustomTimerModal
          show={customModalOpen}
          onClose={() => setCustomModalOpen(false)}
          timerPairs={timerPairs}
          setTimerPairs={setTimerPairs}
          loopTimers={loopTimers}
          setLoopTimers={setLoopTimers}
          theme={theme}
          onStartSequence={startCustomSequence}
        />

        <NotepadModal
          show={notepadModalOpen}
          onClose={() => setNotepadModalOpen(false)}
          theme={theme}
        />

        <Header
          mode={mode}
          setMode={setMode}
          setTimerSeconds={setTimerSeconds}
          setBaseSeconds={setBaseSeconds}
          setRunning={setRunning}
          themeKey={themeKey}
          setThemeKey={setThemeKey}
          themes={themes}
          theme={theme}
          displayMode={displayMode}
          openThemeModal={() => setModalOpen(true)}
          setDisplayMode={setDisplayMode}
          onOpenCustomModal={() => setCustomModalOpen(true)}
          handleShare={handleShare}
          onSetPresetPair={setPresetPair}
          onOpenNotepad={() => setNotepadModalOpen(true)}
        />


        <div className="flex flex-1 flex-col items-center justify-center">
          <div className="w-full max-w-2xl px-2 sm:px-6">
          <TimerDisplay
              mode={mode}
              editing={editing}
              timerSeconds={timerSeconds}
              handleTimerClick={handleTimerClick}
              inputValue={inputValue}
              handleInputChange={handleInputChange}
              inputRef={inputRef}
              handleInputBlurOrEnter={handleInputBlurOrEnter}
              handleInputKeyDown={handleInputKeyDown}
              theme={theme}
              displayMode={displayMode}
              currentPhase={currentPhase}
              selectedPreset={selectedPreset}
              running={running}
              timerPairs={timerPairs}
              currentStep={currentStep}
              onTimeChange={handleTimeChange}
              baseSeconds={baseSeconds}
            />


            <TimerControls
              running={running}
              timerSeconds={timerSeconds}
              handleStart={handleStart}
              handleStop={handleStop}
              handleReset={handleReset}
              handleNextTimer={handleNextTimer}
              theme={theme}
              mode={mode}
            />

            <ThemeModal
              themes={themes}
              themeKey={themeKey}
              setThemeKey={setThemeKey}
              previewThemeKey={previewThemeKey}
              setPreviewThemeKey={setPreviewThemeKey}
              show={modalOpen}
              onClose={() => setModalOpen(false)}
            />
          </div>
        </div>
        <Footer
        theme={theme}
        />
      </div>
    );
  }

  export default App;
